import { useState, useEffect, useMemo, useCallback } from 'react';
import { skillRegistry } from '../../onboarding/registry/SkillRegistry';
import { workspaceRegistry } from '../../onboarding/registry/WorkspaceRegistry';
import { useSkills } from '../../onboarding/apis/skill-registry';
import { useWorkspaces } from '../../onboarding/apis/workspace-registry';
import {
  QuickOnboardingConfig,
  QuickOnboardingTool,
  QuickOnboardingHookReturn,
  ToolType,
  ToolTypeMap,
} from './types';
import { useUserProfile } from '../../../api/profile';

// Tool type mapping for generic configuration
const TOOL_TYPE_MAP: ToolTypeMap = {
  slack: 'workspace',
  jira: 'skill',
};

// Default tool configurations with priorities - using actual registry data
const DEFAULT_TOOL_CONFIGS: Omit<QuickOnboardingTool, 'enabled'>[] = [];

export const useQuickOnboardingConfig = ({
  isCompact = false,
}: { isCompact?: boolean } = {}): QuickOnboardingHookReturn => {
  const {
    data: skills,
    isLoading: skillsPending,
    isError: skillsError,
  } = useSkills();
  const {
    data: workspaces,
    isLoading: workspacesPending,
    isError: workspacesError,
  } = useWorkspaces();
  const [dismissedTools, setDismissedTools] = useState<Set<string>>(new Set());

  const {
    data: userProfile,
    isLoading: profilePending,
    isError: profileError,
  } = useUserProfile();
  const hasPermission = !!userProfile?.permissions?.some(
    (p: string) => p.includes('pleri:Write') || p.includes('*'),
  );

  const storageKey = `plerion-quick-onboarding-dismissed`;
  const maxVisible = isCompact ? 1 : 2;

  // Calculate overall loading and error states
  const isLoading = skillsPending || workspacesPending || profilePending;
  const isError = skillsError || workspacesError || profileError;

  // Load dismissed tools from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const dismissed = JSON.parse(stored) as string[];
        setDismissedTools(new Set(dismissed));
      }
    } catch (error) {
      console.warn('Failed to load dismissed tools from localStorage:', error);
    }
  }, [storageKey]);

  // Save dismissed tools to localStorage
  const saveDismissedTools = useCallback(
    (tools: Set<string>) => {
      try {
        localStorage.setItem(storageKey, JSON.stringify(Array.from(tools)));
      } catch (error) {
        console.warn('Failed to save dismissed tools to localStorage:', error);
      }
    },
    [storageKey],
  );

  // Generic tool configuration function
  const configureTool = useCallback(
    (
      config: Omit<QuickOnboardingTool, 'enabled'>,
      toolType: ToolType,
    ): QuickOnboardingTool => {
      let image = undefined;
      let icon = undefined;
      let enabled = true;

      if (toolType === 'workspace') {
        const workspace = workspaces?.find(
          (w) => w.workspaceType === config.id,
        );
        enabled = !workspace?.enabled;

        const registration = workspaceRegistry.getWorkspace(config.id);
        if (registration) {
          image = registration.config.image;
          icon = registration.config.icon;
        }
      } else if (toolType === 'skill') {
        const skill = skills?.find((s) => s.skillType === config.id);
        enabled = !skill?.enabled;

        const registration = skillRegistry.getSkill(config.id);
        if (registration) {
          image = registration.config.image;
          icon = registration.config.icon;
        }
      }

      return {
        ...config,
        enabled,
        image,
        icon,
      };
    },
    [skills, workspaces],
  );

  // Memoized tools configuration
  const tools: QuickOnboardingTool[] = useMemo(() => {
    return DEFAULT_TOOL_CONFIGS.map((config) => {
      const toolType = TOOL_TYPE_MAP[config.id];
      return configureTool(config, toolType);
    });
  }, [configureTool]);

  // Memoized visible tools - filtered and sorted based on priority and dismissal status
  const visibleTools = useMemo(() => {
    return tools
      .filter((tool) => tool.enabled && !dismissedTools.has(tool.id))
      .sort((a, b) => a.priority - b.priority)
      .slice(0, maxVisible);
  }, [tools, dismissedTools, maxVisible]);

  // Callback to handle tool dismissal
  const handleDismiss = useCallback(
    (toolId: string) => {
      const newDismissedTools = new Set(dismissedTools);
      newDismissedTools.add(toolId);
      setDismissedTools(newDismissedTools);
      saveDismissedTools(newDismissedTools);
    },
    [dismissedTools, saveDismissedTools],
  );

  return {
    tools,
    visibleTools: hasPermission ? visibleTools : [],
    maxVisible: hasPermission ? maxVisible : 0,
    showDismissButton: true,
    storageKey,
    handleDismiss,
    isLoading,
    isError,
  };
};

// Utility function to create custom configurations
export const createQuickOnboardingConfig = (
  customTools: Partial<QuickOnboardingTool>[],
  options: {
    maxVisible?: number;
    showDismissButton?: boolean;
    storageKey?: string;
  } = {},
): QuickOnboardingConfig => {
  const tools: QuickOnboardingTool[] = customTools.map((tool) => ({
    id: tool.id!,
    name: tool.name!,
    description: tool.description!,
    icon: tool.icon!,
    buttonText: tool.buttonText!,
    priority: tool.priority!,
    route: tool.route!,
    category: tool.category!,
    enabled: tool.enabled ?? true,
  }));

  return {
    tools,
    maxVisible: options.maxVisible ?? 4,
    showDismissButton: options.showDismissButton ?? true,
    storageKey: options.storageKey ?? 'plerion-quick-onboarding-dismissed',
  };
};
